import { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import PropTypes from "prop-types";
import useApi from "../hooks/apiService";
import { useToken } from "../hooks/tokenContext";
import { formatDateToBr } from "../utils/dateFormatter";
import { useTranslation } from "react-i18next";
import { InputDate, InputSelect } from "@2solve/tosolve-inputs";

const ModalExport = ({ show, onHide }) => {
  const { t } = useTranslation();
  const { token } = useToken();
  const [truckId, setTruckId] = useState("");
  const [selectedFields, setSelectedFields] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [assetOptions, setAssetOptions] = useState([]);
  const [fieldsOptions, setFieldsOptions] = useState([]);
  const [exportFormat, setExportFormat] = useState();
  const [error, setError] = useState("");
  const { fetchData, postCSV } = useApi();
  const localUser = JSON.parse(localStorage.getItem("user"));
  const [filter, setFilter] = useState();
  const [loading, setLoading] = useState(false);
  const [fieldsKey, setFieldsKey] = useState(0);

  useEffect(() => {
    if (localUser?.user.view_group && token) {
      handleFilterChange(localUser.user.view_group);
    } else {
      handleFilterChange();
    }
  }, [show]);

  const handleFilterChange = async (filter) => {
    if (filter) {
      const response = await fetchData(
        `view-groups/${filter.id}/view-group-items`,
      );

      // Assumindo que `response.assets` é o array de dispositivos:
      const assetLabels = response.data.map((asset) => asset.asset.label);

      setFilter(assetLabels);
    } else {
      setFilter(null);
    }
  };
  // Fetch assets on component mount
  useEffect(() => {
    if (token) {
      const fetchAssets = async () => {
        try {
          const response = await fetchData("/assets");
          if (filter) {
            const filteredAssets = response.data.filter((asset) =>
              filter.includes(asset.label),
            );
            setAssetOptions(filteredAssets);
          } else {
            setAssetOptions(response.data);
          }
        } catch (error) {
          console.error(error);
        }
      };
      fetchAssets();
    }
  }, [filter]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!show) {
      setTruckId("");
      setSelectedFields([]);
      setStartDate("");
      setEndDate("");
      setFieldsOptions([]);
      setExportFormat();
      setError("");
    }
  }, [show]);

  // Fetch fields for selected asset
  const handleAssetChange = async (asset) => {
    setTruckId(asset);

    // limpa seleção e opções atuais
    setSelectedFields([]);
    setFieldsOptions([]);

    // força remount do select de variáveis
    setFieldsKey((k) => k + 1);

    if (!asset) return;

    try {
      const response = await fetchData(`/assets/${asset}/variables`);
      setFieldsOptions(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Handle export data
  const handleExportData = async () => {
    try {
      setLoading(true);
      const selectedAsset = assetOptions.find((asset) => asset.id === truckId);
      if (!selectedAsset) {
        setError(t("Escolha um ativo"));
        setLoading(false);
        return;
      }
      if (!startDate) {
        setError(t("Selecione a data inicial"));
        setLoading(false);
        return;
      }
      if (!endDate) {
        setError(t("Selecione a data final"));
        setLoading(false);
        return;
      }
      if (!exportFormat) {
        setError(t("Selecione o formato desejado"));
        setLoading(false);
        return;
      }

      let startDateTime = new Date(startDate);
      startDateTime.setSeconds(0);

      let endDateTime = new Date(endDate);
      endDateTime.setSeconds(endDateTime.getSeconds() + 59);

      if (startDateTime > endDateTime) {
        setError(t("A data final precisa ser depois da data incial"));
        setLoading(false);
        return;
      }
      if (startDateTime === endDateTime) {
        setError(t("As datas precisam ser diferentes"));
        setLoading(false);
        return;
      }
      if (endDateTime > new Date()) {
        endDateTime = new Date();
      }

      endDateTime = formatDateToBr(endDateTime);
      startDateTime = formatDateToBr(startDateTime);

      if (selectedFields.length === 0) {
        setError(t("Selecione algum campo para exportar"));
        setLoading(false);
        return;
      }

      const bodyRequest = {
        measurement: selectedAsset.label,
        fields: selectedFields,
        start_datetime: startDateTime,
        end_datetime: endDateTime,
        return_type: exportFormat, // Add return type based on selected format
      };

      const response = await postCSV("/measurements/export", bodyRequest);
      const blob = new Blob([response.data], {
        type:
          exportFormat === "csv"
            ? "text/csv"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Exported_Data_${selectedAsset.name}.${exportFormat}`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
      setLoading(false);
      setError("");
      onHide(true);
    } catch (error) {
      setError(t("Não foi possível encontrar dados nesse intervalo de tempo"));
      setLoading(false);
      console.error(error);
    }
  };

  // Options for export format select
  const formatOptions = [
    { label: "CSV", value: "csv" },
    { label: "XLSX", value: "xlsx" },
  ];

  // Options for asset and fields select
  const assetOptionsSelect = assetOptions.map((data) => ({
    label: data.name,
    value: data.id,
  }));

  const fieldsOptionsSelect = fieldsOptions.map((data) => ({
    label: data.name,
    value: data.treated_name,
  }));

  return (
    <Modal
      backdrop="static"
      centered
      size="lg"
      show={show}
      onHide={() => onHide(false)}
    >
      <Modal.Header closeVariant="white" closeButton>
        <Modal.Title>{t("Exportar dados")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <InputSelect
            id="assetSelect"
            placeholder={t("De qual ativo deseja exportar os dados")}
            required={true}
            options={assetOptionsSelect}
            isMultiple={false}
            closeOnSelect={true}
            onChange={(_, value) => handleAssetChange(value)}
            value={truckId}
          />
          <Row>
            <Col>
              <InputDate
                id="formStartDate"
                placeholder={t("Data inicial")}
                type="datetime-local"
                value={startDate}
                required={true}
                onChange={(_, value) => setStartDate(value)}
              />
            </Col>
            <Col>
              <InputDate
                id="formEndDate"
                placeholder={t("Data final")}
                type="datetime-local"
                value={endDate}
                required={true}
                onChange={(_, value) => setEndDate(value)}
              />
            </Col>
          </Row>
          <InputSelect
            key={fieldsKey} // <-- força recriar quando o ativo muda
            id="fieldsSelect"
            placeholder={t("Variáveis para exportar")}
            options={fieldsOptionsSelect}
            isMultiple={true}
            closeOnSelect={false}
            required={true}
            onChange={(_, value) => setSelectedFields(value)}
            value={selectedFields} // estado controlado continua igual
          />
          <InputSelect
            id="formatSelect"
            placeholder={t("Formato de exportação")}
            options={formatOptions}
            required={true}
            isMultiple={false}
            closeOnSelect={true}
            onChange={(_, value) => setExportFormat(value)}
            value={exportFormat}
          />
        </Form>
        {error && (
          <div className="text-danger">
            <br></br>
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={loading ? null : handleExportData}>
          {loading ? <Spinner animation="border" size="sm" /> : t("Exportar")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ModalExport.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default ModalExport;
